import { useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";

import tractorLogin from "../assets/loginFin3.png";
import logo from "../assets/fundiLogo.svg";

export default function Login() {
  const [username, setUsername] = useState("");
  const [password, setPassword] = useState("");
  const [errors, setErrors] = useState({});
  const [apiError, setApiError] = useState("");
  const [loading, setLoading] = useState(false);

  const validate = () => {
    const newErrors = {};

    if (!username.trim()) newErrors.username = "Phone number is required";

    if (!password) newErrors.password = "Password is required";
    else if (password.length < 6) {
      newErrors.password = "Password must be at least 6 characters";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleLogin = async () => {
    setApiError("");

    if (!validate()) return;

    setLoading(true);

    try {
      const res = await axios.post(
        `${process.env.REACT_APP_BACKEND_BASE_URL}/api/v1/auth/users/login`,
        {
          username,
          password,
          userType: "ADMIN",
        },
        {
          headers: {
            "x-source-system": "web",
          },
        }
      );

      if (res.data.success) {
        const user = res.data.data;
        localStorage.setItem("user", JSON.stringify(user));
        window.location.href = "/dashboard";
      } else {
        setApiError(res.data.message || "Login failed.");
      }
    } catch (err) {
      setApiError("Invalid credentials. Try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-100 text-slate-900 dark:bg-slate-950 dark:text-white">
      <div className="grid min-h-screen grid-cols-1 lg:grid-cols-[minmax(480px,560px)_1fr]">
        {/* LEFT PANEL */}
        <div className="relative flex items-center justify-center border-r border-slate-200 bg-white px-6 py-10 dark:border-slate-800 dark:bg-slate-900">
          <div className="absolute inset-x-0 top-0 h-40 bg-gradient-to-b from-slate-50 to-transparent dark:from-slate-800/40 dark:to-transparent" />

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.35 }}
            className="relative z-10 w-full max-w-md"
          >
            <div className="rounded-[28px] border border-slate-200 bg-white p-8 shadow-[0_18px_50px_rgba(15,23,42,0.08)] dark:border-slate-800 dark:bg-slate-900 dark:shadow-[0_18px_50px_rgba(0,0,0,0.32)]">
              <div className="mb-8 text-center">
                <div className="mb-5 flex justify-center">
                  <div className="flex h-20 w-20 items-center justify-center rounded-3xl border border-slate-200 bg-slate-50 shadow-sm dark:border-slate-800 dark:bg-slate-800/70">
                    <img src={logo} alt="Logo" className="h-12 w-auto" />
                  </div>
                </div>

                <div className="inline-flex items-center rounded-full border border-slate-200 bg-slate-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500 dark:border-slate-700 dark:bg-slate-800/60 dark:text-slate-400">
                  Admin Access
                </div>

                <h1 className="mt-4 text-3xl font-semibold tracking-tight text-slate-900 dark:text-white">
                  Welcome back
                </h1>

                <p className="mt-2 text-sm leading-6 text-slate-500 dark:text-slate-400">
                  Sign in to access operations, technician workflows, customers,
                  and task management.
                </p>
              </div>

              {apiError && (
                <div className="mb-5 rounded-2xl border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
                  {apiError}
                </div>
              )}

              <div className="space-y-5">
                <div>
                  <label className="mb-2 block text-sm font-medium text-slate-700 dark:text-slate-300">
                    Phone Number
                  </label>

                  <input
                    type="text"
                    value={username}
                    onChange={(e) => setUsername(e.target.value)}
                    placeholder="Enter phone number"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-4 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 ${
                      errors.username
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-100 dark:border-red-500/60 dark:bg-red-500/10 dark:focus:ring-red-500/10"
                        : "border-slate-300 bg-white focus:border-slate-900 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:focus:border-white dark:focus:ring-slate-800"
                    }`}
                  />

                  {errors.username && (
                    <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                      {errors.username}
                    </p>
                  )}
                </div>

                <div>
                  <div className="mb-2 flex items-center justify-between gap-3">
                    <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">
                      Password
                    </label>

                    <button
                      type="button"
                      className="text-xs font-medium text-slate-500 transition hover:text-slate-900 dark:text-slate-400 dark:hover:text-white"
                    >
                      Forgot password?
                    </button>
                  </div>

                  <input
                    type="password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    placeholder="Enter password"
                    className={`w-full rounded-2xl border px-4 py-3 text-sm text-slate-900 outline-none transition placeholder:text-slate-400 focus:ring-4 dark:bg-slate-900 dark:text-white dark:placeholder:text-slate-500 ${
                      errors.password
                        ? "border-red-400 bg-red-50 focus:border-red-500 focus:ring-red-100 dark:border-red-500/60 dark:bg-red-500/10 dark:focus:ring-red-500/10"
                        : "border-slate-300 bg-white focus:border-slate-900 focus:ring-slate-200 dark:border-slate-700 dark:bg-slate-900 dark:focus:border-white dark:focus:ring-slate-800"
                    }`}
                  />

                  {errors.password && (
                    <p className="mt-2 text-xs font-medium text-red-600 dark:text-red-400">
                      {errors.password}
                    </p>
                  )}
                </div>

                <button
                  onClick={handleLogin}
                  disabled={loading}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-black px-5 py-3.5 text-sm font-semibold text-white transition hover:opacity-90 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-white dark:text-black"
                >
                  {loading && (
                    <span className="h-4 w-4 animate-spin rounded-full border-2 border-white/80 border-t-transparent dark:border-black/70 dark:border-t-transparent" />
                  )}
                  {loading ? "Logging in..." : "Sign In"}
                </button>
              </div>

              <div className="mt-8 border-t border-slate-200 pt-5 text-center dark:border-slate-800">
                <p className="text-xs leading-5 text-slate-500 dark:text-slate-400">
                  Protected access for authorized platform administrators.
                </p>
              </div>
            </div>
          </motion.div>
        </div>

        {/* RIGHT PANEL */}
        <div className="relative hidden overflow-hidden lg:block">
          {/* image */}
          <div
            className="absolute inset-0 bg-cover bg-center scale-[1.02]"
            style={{ backgroundImage: `url(${tractorLogin})` }}
          />

          {/* stronger layered overlays */}
          <div className="absolute inset-0 bg-slate-950/45" />
          <div className="absolute inset-0 bg-gradient-to-br from-slate-950/85 via-slate-950/45 to-slate-950/70" />
          <div className="absolute inset-0 bg-gradient-to-t from-slate-950/80 via-transparent to-slate-950/25" />

          {/* subtle highlight */}
          <div className="absolute -left-24 top-16 h-72 w-72 rounded-full bg-white/10 blur-3xl" />
          <div className="absolute bottom-0 right-0 h-80 w-80 rounded-full bg-slate-700/30 blur-3xl" />

          <div className="relative z-10 flex h-full flex-col justify-between p-10 xl:p-14">
            <div className="flex justify-end">
              <div className="rounded-full border border-white/20 bg-slate-900/45 px-4 py-2 text-xs font-semibold uppercase tracking-[0.16em] text-white shadow-lg backdrop-blur-xl">
                Admin Access
              </div>
            </div>

            {/* main content block */}
            <div className="max-w-2xl">
              <div className="translate-x-6 xl:translate-x-12 rounded-[20px] border border-white/12 bg-slate-700/25 p-8 shadow-[0_20px_60px_rgba(0,0,0,0.35)] backdrop-blur-xl xl:p-10">
                <div className="inline-flex items-center rounded-full border border-white/15 bg-white/10 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.16em] text-white/85">
                  Platform Overview
                </div>

                <h2 className="mt-5 text-4xl font-semibold leading-tight tracking-tight text-white [text-shadow:0_2px_12px_rgba(0,0,0,0.35)] xl:text-5xl">
                  Manage field operations with clarity, speed, and control.
                </h2>

                <p className="mt-5 max-w-xl text-base leading-8 text-white/85">
                  Track tasks, assign technicians, monitor customer work, and
                  coordinate service delivery from a professional unified
                  operations console built for day-to-day execution.
                </p>

                <div className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-3">
                  <div className="rounded-2xl border border-white/12 bg-white/10 p-4 shadow-sm backdrop-blur-md">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/65">
                      Visibility
                    </p>
                    <p className="mt-2 text-sm font-medium leading-6 text-white">
                      Real-time task oversight
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/12 bg-white/10 p-4 shadow-sm backdrop-blur-md">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/65">
                      Coordination
                    </p>
                    <p className="mt-2 text-sm font-medium leading-6 text-white">
                      Technician assignment flow
                    </p>
                  </div>

                  <div className="rounded-2xl border border-white/12 bg-white/10 p-4 shadow-sm backdrop-blur-md">
                    <p className="text-[11px] font-semibold uppercase tracking-[0.14em] text-white/65">
                      Control
                    </p>
                    <p className="mt-2 text-sm font-medium leading-6 text-white">
                      Secure admin access
                    </p>
                  </div>
                </div>
              </div>
            </div>

            <div className="flex items-center justify-between gap-4 text-xs text-white/70">
              <span className="rounded-full border border-white/10 bg-slate-900/35 px-3 py-1.5 backdrop-blur-md">
                @2026 Agri Fundi E.A.
              </span>

              <span className="rounded-full border border-white/10 bg-slate-900/35 px-3 py-1.5 backdrop-blur-md">
                All Rights Reserved
              </span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}